import React, { useEffect, useState } from 'react';
import { Modal, Form, Input } from 'antd';
import s from './s.module.less';
import commonS from 'styles/common.module.less';
import { TMedicationContent } from 'pages/operation/MedicationListPageManagement/type';

interface MedicationContentModalProps {
    visible: boolean;
    onCancel: () => void;
    onSave: (data: TMedicationContent) => void;
    initialData?: TMedicationContent;
    medicationName?: string;
}

const MedicationContentModal: React.FC<MedicationContentModalProps> = ({
    visible,
    onCancel,
    onSave,
    initialData,
    medicationName = 'Medication Content',
}) => {
    const [form] = Form.useForm<TMedicationContent>();
    const [charCounts, setCharCounts] = useState({
        title: 0,
        subText: 0,
    });

    // 更新字符计数
    const updateCharCounts = (data: Partial<TMedicationContent>) => {
        setCharCounts({
            title: (data.title || '').length,
            subText: (data.subText || '').length,
        });
    };

    // 当模态框打开或初始数据变化时，设置表单数据
    useEffect(() => {
        if (visible) {
            if (initialData) {
                form.setFieldsValue(initialData);
                updateCharCounts(initialData);
            } else {
                // 设置默认值
                const defaultValues = {
                    title: '',
                    subText: '',
                };
                form.setFieldsValue(defaultValues);
                setCharCounts({
                    title: 0,
                    subText: 0,
                });
            }
        }
    }, [visible, initialData, form]);

    // 处理表单提交
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            onSave(values);
        } catch (error) {
            console.error('表单验证失败:', error);
        }
    };

    // 处理取消
    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    // 处理字段值变化
    const handleFieldChange = (fieldName: keyof TMedicationContent, value: string) => {
        form.setFieldValue(fieldName, value);

        // 更新字符计数
        const newCounts = { ...charCounts };
        if (fieldName === 'title') {
            newCounts.title = value.length;
        } else if (fieldName === 'subText') {
            newCounts.subText = value.length;
        }
        setCharCounts(newCounts);
    };

    return (
        <Modal
            title={initialData ? `Edit ${medicationName} Content` : `Add ${medicationName} Content`}
            className={commonS.modalFixHeightWrap}
            open={visible}
            onCancel={handleCancel}
            okText="Save changes"
            onOk={handleSubmit}
            width={543}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                className={s.form}
            >
                {/* Title Field */}
                <Form.Item
                    label="Title"
                    name="title"
                    rules={[
                        { required: true, message: 'Please enter title' },
                        { max: 70, message: 'Title cannot exceed 70 characters' },
                    ]}
                >
                    <Input
                        placeholder="Top ADHD medications: What you need to know"
                        maxLength={70}
                        onChange={(e) => handleFieldChange('title', e.target.value)}
                    />
                </Form.Item>

                {/* Subtext Field */}
                <Form.Item
                    label="Subtext"
                    name="subText"
                    rules={[
                        { required: true, message: 'Please enter subtext' },
                        { max: 3000, message: 'Subtext cannot exceed 3000 characters' },
                    ]}
                >
                    <Input.TextArea
                        placeholder="Detailed information about ADHD medications, including types, effects, and considerations for patients and healthcare providers."
                        maxLength={3000}
                        rows={6}
                        onChange={(e) => handleFieldChange('subText', e.target.value)}
                        showCount
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default MedicationContentModal;
